/*
 * customsirge_megamenu.js — the two-pane nav panel controller (desktop megamenu + mobile drawer).
 *
 * WHAT IT OWNS: moving one `is-active` class between `[data-cs-mega-tab]` links and the
 * `[data-cs-mega-pane]` elements that share a `[data-cs-mega]` root. Liquid renders the default
 * selection server-side, so every panel is complete and correct with this file absent — the script
 * only ever MOVES the class, never creates the content.
 *
 * ARCHITECTURE: everything is delegated off `document`. Nothing is bound per element, so a
 * theme-editor re-render of the header needs no re-init and there is no hook into theme.js's
 * header section. `mouseover` and `focusin` bubble (`pointerenter` does not), which is what makes
 * the delegated form possible.
 *
 * ROLES ARE APPLIED HERE, NEVER IN LIQUID: with scripting off the DOM stays an honest plain nav of
 * links, which is what the .no-js CSS branch renders as stacked captioned lists.
 */
(function () {
  'use strict';

  var ROOT = '[data-cs-mega]';
  /* ONLY a child that actually controls a pane is a tab. A children-only panel (PRO STAFF,
     WHOLESALE) has none, so it gets no tablist, no roving tabindex and no selection — its links
     stay plain links in natural tab order. Applying the roving tabindex there made children 2..N
     unreachable by Tab, and arrowing onto a paneless child blanked the whole panel. */
  var TAB = '[data-cs-mega-tab][aria-controls]';
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  function rootOf(el) {
    return el && el.closest ? el.closest(ROOT) : null;
  }

  function tabsIn(root) {
    return Array.prototype.slice.call(root.querySelectorAll(TAB));
  }

  /* The drawer panel's height is an inline px value theme.js writes once, when the collapsible
     opens. Panes differ in length, so switching child without re-measuring would clip the longer
     list or leave a gap under the shorter one. This repeats theme.js's own arithmetic
     (`.collapsible-content__inner` offsetHeight) rather than calling into theme.collapsibles, whose
     auto-height path calls an `Element.prototype.css` helper the theme never defines. No-ops on
     desktop (no .collapsible-content ancestor) and on a Liquid-opened `height:auto` panel. */
  function syncDrawerHeight(root) {
    var panel = root.closest('.collapsible-content');
    if (!panel || !panel.classList.contains('is-open')) return;
    if (!panel.style.height || panel.style.height === 'auto') return;
    var inner = panel.querySelector('.collapsible-content__inner');
    if (inner) panel.style.height = inner.offsetHeight + 'px';
  }

  function activate(tab) {
    var root = rootOf(tab);
    if (!root || tab.classList.contains('is-active')) return;
    /* Never switch while focus is inside the pane we are about to hide: hiding the focused element
       drops focus to <body>, and theme.js then treats the panel as unfocused and closes it. A
       pointer drifting over the column while the user is tabbing through links is enough. */
    var focused = document.activeElement;
    if (focused && focused !== document.body && root.contains(focused) && focused.closest('[data-cs-mega-pane]')) return;
    var key = tab.getAttribute('data-cs-mega-tab');

    tabsIn(root).forEach(function (el) {
      var on = el === tab;
      el.classList.toggle('is-active', on);
      if (el.hasAttribute('role')) el.setAttribute('aria-selected', on ? 'true' : 'false');
      if (el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', on ? 'true' : 'false');
      el.tabIndex = on ? 0 : -1;
    });

    var hasImage = false;
    Array.prototype.forEach.call(root.querySelectorAll('[data-cs-mega-pane]'), function (pane) {
      var on = pane.getAttribute('data-cs-mega-pane') === key;
      pane.classList.toggle('is-active', on);
      if (on && pane.classList.contains('customsirge-mega__media-slot')) hasImage = true;
    });

    /* Collapse the image half when the selected child has no collection photo, so the lists take
       the width instead of sitting beside an empty 690px column. Liquid sets this attribute for the
       default child; from here on the controller maintains it. */
    var media = root.querySelector('.customsirge-mega__media');
    if (media) {
      if (hasImage) media.removeAttribute('data-cs-empty');
      else media.setAttribute('data-cs-empty', 'true');
    }

    syncDrawerHeight(root);
  }

  /* ---- pointer ---------------------------------------------------------------------------- */

  document.addEventListener(
    'mouseover',
    function (evt) {
      if (!finePointer.matches) return;
      var tab = evt.target.closest && evt.target.closest(TAB);
      if (tab) activate(tab);
    },
    { passive: true }
  );

  /* Coarse pointer: first tap selects, second tap follows the link. A child with no grandchildren
     carries no aria-controls, so it is not a selector and navigates on the first tap. */
  document.addEventListener('click', function (evt) {
    if (finePointer.matches) return;
    var tab = evt.target.closest && evt.target.closest(TAB);
    if (!tab || !tab.hasAttribute('aria-controls')) return;
    evt.preventDefault();
    if (tab.classList.contains('is-active') && tab.closest('.customsirge-mmenu')) {
      /* Drawer accordion: tapping the open category closes it. */
      var root = rootOf(tab);
      tabsIn(root).forEach(function (el) { el.classList.remove('is-active'); el.setAttribute('aria-expanded', 'false'); });
      Array.prototype.forEach.call(root.querySelectorAll('[data-cs-mega-pane]'), function (pane) { pane.classList.remove('is-active'); });
      syncDrawerHeight(root);
      return;
    }
    if (tab.classList.contains('is-active')) return;
    activate(tab);
  });

  /* ---- keyboard --------------------------------------------------------------------------- */

  /* A pointer press focuses its target BEFORE the click fires. Without this flag the focusin
     handler below selected the row on touch, so by the time the click handler ran the row was
     already active, it took the "already selected" branch, and the FIRST tap both selected and
     navigated — the two-tap model silently collapsed to one tap. MEASURED: on a real page.tap the
     click handler logged `target=customsirge-mmenu__child is-active` and bailed.
     Cleared on a timeout rather than on click, because a pointerdown that never becomes a click
     (a scroll, a drag off the row) must not leave focus activation disabled. */
  var pointerFocus = false;
  document.addEventListener(
    'pointerdown',
    function () {
      pointerFocus = true;
      window.setTimeout(function () {
        pointerFocus = false;
      }, 400);
    },
    { passive: true, capture: true }
  );

  /* Focus-driven activation is for KEYBOARD users: arrowing or tabbing onto a tab selects it. A
     pointer's own focus is ignored — the pointer handlers above already decide what a press means. */
  document.addEventListener('focusin', function (evt) {
    if (pointerFocus) return;
    var tab = evt.target.closest && evt.target.closest(TAB);
    if (tab) activate(tab);
  });

  document.addEventListener('keydown', function (evt) {
    var tab = evt.target.closest && evt.target.closest(TAB);
    if (!tab) return;
    var root = rootOf(tab);
    if (!root) return;
    var tabs = tabsIn(root);
    var i = tabs.indexOf(tab);
    var next = null;

    if (evt.key === 'ArrowDown' || evt.key === 'ArrowRight') next = tabs[i + 1] || tabs[0];
    else if (evt.key === 'ArrowUp' || evt.key === 'ArrowLeft') next = tabs[i - 1] || tabs[tabs.length - 1];
    else if (evt.key === 'Home') next = tabs[0];
    else if (evt.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;

    evt.preventDefault();
    activate(next);
    next.focus();
  });

  /* ---- init ------------------------------------------------------------------------------- */

  /* Idempotent: normalises roles and the roving tabindex from whatever Liquid rendered. The roving
     tabindex makes the children column ONE tab stop, so Tab out of it lands in the selected pane
     and every grandchild is reachable — with six natural tab stops the panes sit behind a long
     run of column links instead. */
  function init(scope) {
    var roots = (scope || document).querySelectorAll(ROOT);
    Array.prototype.forEach.call(roots, function (root) {
      var tabs = tabsIn(root);
      /* A panel with no panes is just a list of links: no tablist, no roles, no roving tabindex.
         Announcing "tablist" with zero tabs, or making links 2..N unreachable by Tab, would both be
         worse than leaving the markup alone. */
      if (!tabs.length) return;

      var list = root.querySelector('[data-cs-mega-tablist]');
      if (list && !list.hasAttribute('role')) {
        list.setAttribute('role', 'tablist');
        list.setAttribute('aria-orientation', 'vertical');
      }
      tabs.forEach(function (el) {
        var on = el.classList.contains('is-active');
        el.setAttribute('role', 'tab');
        el.setAttribute('aria-selected', on ? 'true' : 'false');
        if (el.parentElement && el.parentElement.tagName === 'LI') {
          el.parentElement.setAttribute('role', 'presentation');
        }
        el.tabIndex = on ? 0 : -1;
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-cs-mega-pane]'), function (pane) {
        if (pane.tagName === 'UL' && !pane.hasAttribute('role')) pane.setAttribute('role', 'tabpanel');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target);
  });
})();
